import {
  envHeaderValue,
  saveKeyScript,
  snippets,
  stripEmphasis,
} from '@/features/mcp-access/generate';
import {
  CHATGPT_URLS,
  CHATGPT_ADD_SERVER_STEPS,
  CLAUDE_URLS,
  claudeInstallLink,
  claudeOrgInstallLink,
  cursorInstallLink,
  cursorWebInstallLink,
} from '@/features/mcp-access/links';
import type { McpSnippet } from '@/features/mcp-access/types';
import type {
  AiAppId,
  AiAppRecipe,
  AiAppsAudience,
  AiAppsServer,
  ClaudePlan,
  RecipeSection,
  RecipeStep,
} from './types';

export const AI_APPS: AiAppId[] = [
  'claude',
  'chatgpt',
  'claude-code',
  'cursor',
];

export const AI_APP_LABELS: Record<AiAppId, string> = {
  claude: 'Claude',
  chatgpt: 'ChatGPT',
  'claude-code': 'Claude Code',
  cursor: 'Cursor',
};

export interface RecipeContext {
  audience: AiAppsAudience;
  claudePlan: ClaudePlan;
  /** Absolute link to this page, for the message admins send their team. */
  pageUrl: string;
}

export const canSignIn = (server: AiAppsServer, app: AiAppId): boolean =>
  Boolean(server.profile.serverUrl) &&
  server.profile.authModes.includes('oauth') &&
  (server.signIn ? server.signIn.apps.includes(app) : true);

const shell = (id: string, label: string, text: string): McpSnippet => ({
  id,
  label,
  text,
  language: 'shell',
});

const signInStep = (server: AiAppsServer): RecipeStep => ({
  id: 'sign-in',
  text: server.signIn?.hint ?? 'Sign in as yourself and click **Allow**.',
});

const skillsDownloadStep = (server: AiAppsServer): RecipeStep => ({
  id: 'download-skills',
  text: 'Download the skills. They hold instructions only, no keys.',
  downloads: server.skills,
});

export const teamMessage = (server: AiAppsServer, pageUrl: string): string => {
  const { name } = server.profile;
  return [
    `You can now use ${name} in Claude, as yourself.`,
    '',
    `1. In Claude, open Customize > Connectors: ${CLAUDE_URLS.connectors}`,
    `2. Find ${name} and click Connect. ${stripEmphasis(signInStep(server).text)}`,
    `3. In a new chat, ask: "${server.verify.prompt}" ${server.verify.expect}`,
    '',
    `Help for Claude, ChatGPT, Claude Code and Cursor: ${pageUrl}`,
  ].join('\n');
};

const getKeyStep = (
  server: AiAppsServer,
  audience: AiAppsAudience,
): RecipeStep => {
  const { apiKey, issuedBy } = server.keyAccess!;
  if (audience === 'admin' && server.manageKeysPath) {
    return {
      id: 'get-key',
      text: `Create a ${apiKey.keyName} on the server's page in Capitol, under **Keys**, and copy it. Give every app its own key so you can revoke one alone.`,
      link: {
        label: "Open the server's page",
        href: server.manageKeysPath,
        internal: true,
      },
    };
  }
  return {
    id: 'get-key',
    text: `Ask ${issuedBy} for your own ${apiKey.keyName}. It starts with ${apiKey.prefix}. Never paste it into a chat or share it.`,
  };
};

const saveKeyStep = (server: AiAppsServer, withEnv = false): RecipeStep => {
  const key = server.keyAccess!;
  return {
    id: 'save-key',
    text: withEnv
      ? 'Copy the key, then run this in a terminal on your Mac. It saves the key in a file only you can read, and lets Cursor read it from a variable. Then quit and reopen Cursor.'
      : 'Copy the key, then run this in a terminal on your Mac. It saves the key in a file only you can read.',
    snippet: shell(
      'save_key',
      'Save the key',
      saveKeyScript(key.keyFile, withEnv ? key.envVar : undefined),
    ),
  };
};

/** `Authorization: Bearer $(cat ~/.x.key)` and friends, from the key spec. */
const headerFromFile = (server: AiAppsServer) => {
  const { apiKey, keyFile } = server.keyAccess!;
  const value = `$(cat ${keyFile})`;
  return `${apiKey.headerName}: ${apiKey.scheme ? `${apiKey.scheme} ` : ''}${value}`;
};

const unavailable = (
  server: AiAppsServer,
  app: AiAppId,
  reason: string,
  sections: RecipeSection[] = [],
): AiAppRecipe => ({
  app,
  available: false,
  oneClick: false,
  summary: sections.length ? 'Needs a key' : 'Not available',
  unavailableReason: reason,
  tryInstead: AI_APPS.filter(
    (other) =>
      other !== app &&
      (canSignIn(server, other) ||
        (Boolean(server.keyAccess) &&
          (other === 'cursor' || other === 'claude-code'))),
  ),
  sections,
});

const keyOnlyReason = (server: AiAppsServer, app: AiAppId) =>
  server.signIn?.blocked?.[app] ??
  `${AI_APP_LABELS[app]} connects by signing in, and ${server.profile.name} only accepts a key, so it can't be added there yet.`;

const claudeDesktopBridge = (
  server: AiAppsServer,
  audience: AiAppsAudience,
): RecipeSection => {
  const key = server.keyAccess!;
  const headerFile = key.headerFile.replace(/^~\//, '/Users/YOU/');
  return {
    id: 'claude-desktop-bridge',
    title: 'Claude Desktop only: the local bridge',
    tone: 'optional',
    note: 'Claude Desktop can reach a key-based server through a small local program, mcp-remote. It needs Node.js, and it works in the desktop app only.',
    steps: [
      getKeyStep(server, audience),
      saveKeyStep(server),
      {
        id: 'header-file',
        text: 'Turn the key into a header file, also readable only by you.',
        snippet: shell(
          'header_file',
          'Header file',
          `printf '%s\\n' "${headerFromFile(server)}" > ${key.headerFile} && chmod 600 ${key.headerFile}`,
        ),
      },
      {
        id: 'desktop-config',
        text: 'In Claude Desktop, open **Settings > Developer > Edit Config** and add this server. Replace YOU with your Mac user name, save, then quit and reopen Claude.',
        snippet: {
          id: 'desktop_config',
          label: 'claude_desktop_config.json',
          language: 'json',
          text: JSON.stringify(
            {
              mcpServers: {
                [server.configName]: {
                  command: 'npx',
                  args: [
                    '-y',
                    'mcp-remote@0.15.2',
                    key.serverUrl,
                    '--header-file',
                    headerFile,
                    '--transport',
                    'http-only',
                  ],
                },
              },
            },
            null,
            2,
          ),
        },
      },
    ],
  };
};

const claudeRecipe = (
  server: AiAppsServer,
  ctx: RecipeContext,
): AiAppRecipe => {
  const { name, serverUrl } = server.profile;
  if (!canSignIn(server, 'claude') || !serverUrl) {
    return unavailable(
      server,
      'claude',
      keyOnlyReason(server, 'claude'),
      server.keyAccess ? [claudeDesktopBridge(server, ctx.audience)] : [],
    );
  }

  const sections: RecipeSection[] = [];
  if (ctx.claudePlan === 'team') {
    if (ctx.audience === 'admin') {
      sections.push({
        id: 'claude-owner',
        title: 'First, set it up once for your team',
        tone: 'admin',
        note: `Only a Claude Owner can do this (on Enterprise, also a role that manages the organization's libraries).${
          server.keyAccess
            ? " Never put anyone's member key in an organization connector: Claude would send it for everyone."
            : ''
        }`,
        steps: [
          {
            id: 'add-org-connector',
            text: 'Click **Add to Claude organization**. Claude opens **Organization settings > Connectors** with the name and URL filled in.',
            link: {
              label: 'Add to Claude organization',
              href: claudeOrgInstallLink(name, serverUrl),
            },
          },
          {
            id: 'org-auth',
            text: "If Claude asks how people connect, choose **Sign in now** and **Use Claude's published identity (Recommended)**. Leave **Request headers** empty, then click **Add**.",
          },
          ...(server.plugin
            ? [
                {
                  id: 'org-plugin',
                  text: `Download the ${name} plugin. In **Organization settings > Plugins & skills**, click **Add**, then **Upload a plugin**, and choose the file.`,
                  downloads: [server.plugin],
                  link: {
                    label: 'Open Plugins & skills',
                    href: CLAUDE_URLS.orgPluginsAndSkills,
                  },
                },
                {
                  id: 'org-default-access',
                  text: "Open the plugin's row menu and set **Default access** to **Installed by default**, so everyone gets the skills without searching.",
                },
              ]
            : []),
          {
            id: 'tell-team',
            text: 'Send your team this message. It contains no keys.',
            snippet: {
              id: 'team_message',
              label: 'Message for your team',
              text: teamMessage(server, ctx.pageUrl),
              language: 'text',
            },
          },
        ],
      });
    }
    sections.push({
      id: 'claude-connect',
      title:
        ctx.audience === 'admin' ? 'Then connect yourself' : `Connect ${name}`,
      steps: [
        {
          id: 'open-connectors',
          text: 'Open **Customize > Connectors** in Claude.',
          link: {
            label: 'Open Claude Connectors',
            href: CLAUDE_URLS.connectors,
          },
        },
        {
          id: 'connect',
          text: `Find **${name}**, marked **Custom**, and click **Connect**.`,
        },
        signInStep(server),
      ],
    });
    if (ctx.audience === 'member') {
      sections.push({
        id: 'claude-not-listed',
        title: `Don't see ${name}?`,
        tone: 'optional',
        note: `Your Claude Owner adds it once for the whole organization. Send them this page.`,
        steps: [
          {
            id: 'share-page',
            text: 'Copy the link to this page.',
            snippet: {
              id: 'page_link',
              label: 'Link to this page',
              text: ctx.pageUrl,
              language: 'text',
            },
          },
        ],
      });
    }
  } else {
    sections.push({
      id: 'claude-personal',
      title: `Add ${name} to Claude`,
      note: 'On the Free plan you can add one custom connector.',
      steps: [
        {
          id: 'add',
          text: `Click **Add to Claude**. Claude opens **Add custom connector** with the name and URL filled in. Check the URL is ${serverUrl}, then click **Add**.`,
          link: {
            label: 'Add to Claude',
            href: claudeInstallLink(name, serverUrl),
          },
        },
        { id: 'connect', text: `Click **Connect** next to **${name}**.` },
        signInStep(server),
      ],
    });
  }

  if (
    server.plugin &&
    !(ctx.claudePlan === 'team' && ctx.audience === 'admin')
  ) {
    sections.push({
      id: 'claude-skills',
      title: 'Add the skills (optional)',
      tone: 'optional',
      note:
        ctx.claudePlan === 'team'
          ? `Your Owner can install the ${name} skills for everyone. If they aren't under **Customize > Skills**, and your organization lets you add plugins, add the plugin yourself.`
          : `The plugin teaches Claude the ${name} workflows. It lists the same connector, which shows as connected once you've connected it above.`,
      steps: [
        {
          id: 'plugin',
          text: 'Download the plugin. In **Customize > Plugins**, click **Add**, then **Upload plugin**, and choose the file.',
          downloads: [server.plugin],
          link: { label: 'Open Claude Plugins', href: CLAUDE_URLS.plugins },
        },
      ],
    });
  }

  sections.push({
    id: 'claude-elsewhere',
    title: 'Desktop, mobile and Claude Code',
    tone: 'optional',
    note: 'The same connection works in the Claude desktop and mobile apps, and in Claude Code when you sign in to it with the same Claude account.',
    steps: [],
  });

  return {
    app: 'claude',
    available: true,
    oneClick: true,
    summary: 'Sign in',
    sections,
    verify: server.verify,
  };
};

const chatgptRecipe = (
  server: AiAppsServer,
  ctx: RecipeContext,
): AiAppRecipe => {
  const { name, serverUrl } = server.profile;
  if (!canSignIn(server, 'chatgpt') || !serverUrl) {
    return unavailable(
      server,
      'chatgpt',
      server.signIn?.blocked?.chatgpt ??
        `ChatGPT only connects to servers that sign you in or need no sign-in, and ${name} only accepts a key.`,
    );
  }

  const addSteps: RecipeStep[] = [
    {
      id: 'open-plugins',
      text: CHATGPT_ADD_SERVER_STEPS.open,
      link: { label: 'Open ChatGPT Plugins', href: CHATGPT_URLS.plugins },
    },
    { id: 'add-server', text: CHATGPT_ADD_SERVER_STEPS.add },
    {
      id: 'name-url',
      text: CHATGPT_ADD_SERVER_STEPS.nameAndUrl(name),
      snippet: snippets.serverUrl(server.profile),
    },
    { id: 'auth', text: CHATGPT_ADD_SERVER_STEPS.create(true) },
    signInStep(server),
  ];
  const useStep: RecipeStep = {
    id: 'mention',
    text: `In a chat, type **@${name}** and ask your question.`,
  };

  const sections: RecipeSection[] = [];
  if (ctx.audience === 'admin') {
    sections.push(
      {
        id: 'chatgpt-admin',
        title: 'Add it for your workspace',
        tone: 'admin',
        note: 'Workspace owners and admins add it once. Each member still signs in as themselves.',
        steps: [
          ...addSteps,
          {
            id: 'share',
            text: 'Open the plugin, click **•••**, then **Share plugin**. Under **Who has access**, choose **Visible in workspace directory** and click **Save**.',
          },
        ],
      },
      { id: 'chatgpt-use', title: 'Then use it', steps: [useStep] },
    );
  } else {
    sections.push(
      {
        id: 'chatgpt-install',
        title: `Install ${name}`,
        steps: [
          addSteps[0],
          {
            id: 'install',
            text: `Find **${name}** among your workspace's plugins and click **Install**.`,
          },
          signInStep(server),
          useStep,
        ],
      },
      {
        id: 'chatgpt-self-add',
        title: 'Not listed? Add it yourself',
        tone: 'optional',
        note: 'Only if your workspace lets members create plugins with MCP servers. Otherwise ask a workspace admin to add it.',
        steps: addSteps,
      },
    );
  }

  if (server.skills.length) {
    sections.push({
      id: 'chatgpt-skills',
      title: 'Add the skills (optional)',
      tone: 'optional',
      note: 'Skills need ChatGPT Business, Enterprise, Healthcare or Edu.',
      steps: [
        skillsDownloadStep(server),
        {
          id: 'upload-skills',
          text: 'In **Plugins**, open the **Skills** tab, click **Create**, then **Upload from your computer**. Upload each file.',
        },
      ],
    });
  }

  return {
    app: 'chatgpt',
    available: true,
    oneClick: false,
    summary: 'Sign in',
    requirement:
      "Needs ChatGPT Business, Enterprise or Edu, on the web. On Pro only the read tools work; Plus, Go and Free can't add custom servers.",
    sections,
    verify: server.verify,
  };
};

const claudeCodeRecipe = (
  server: AiAppsServer,
  ctx: RecipeContext,
): AiAppRecipe => {
  const { name, serverUrl } = server.profile;
  const skillsSection: RecipeSection[] = server.skills.length
    ? [
        {
          id: 'claude-code-skills',
          title: 'Add the skills (optional)',
          tone: 'optional',
          note: 'Skills and plugins your Claude organization installs reach Claude Code by themselves when you sign in to it with that account.',
          steps: [
            skillsDownloadStep(server),
            {
              id: 'unzip-skills',
              text: 'Unzip them into your skills folder.',
              snippet: shell(
                'unzip_skills',
                'Install the skills',
                [
                  'mkdir -p ~/.claude/skills && cd ~/.claude/skills',
                  ...server.skills.map(
                    (skill) => `unzip -o ~/Downloads/${skill.fileName}`,
                  ),
                ].join('\n'),
              ),
            },
          ],
        },
      ]
    : [];

  if (canSignIn(server, 'claude-code') && serverUrl) {
    const cliName = server.signIn?.configName ?? server.configName;
    return {
      app: 'claude-code',
      available: true,
      oneClick: false,
      summary: 'One command',
      sections: [
        ...(canSignIn(server, 'claude')
          ? [
              {
                id: 'claude-code-synced',
                title: 'Already connected in Claude?',
                tone: 'optional' as const,
                note: `If you connected ${name} in Claude and sign in to Claude Code with the same Claude account, it's already there: run **/mcp** and look under claude.ai.`,
                steps: [],
              },
            ]
          : []),
        {
          id: 'claude-code-add',
          title: 'Add it with one command',
          steps: [
            {
              id: 'command',
              text: 'Run this in a terminal.',
              snippet: shell(
                'claude_code',
                'Claude Code command',
                `claude mcp add --transport http --scope user ${cliName} ${serverUrl}`,
              ),
            },
            {
              id: 'authenticate',
              text: `Start Claude Code, run **/mcp**, choose **${cliName}**, then **Authenticate**.`,
            },
            signInStep(server),
          ],
        },
        ...skillsSection,
      ],
      verify: server.verify,
    };
  }

  if (!server.keyAccess) {
    return unavailable(
      server,
      'claude-code',
      keyOnlyReason(server, 'claude-code'),
    );
  }
  const key = server.keyAccess;
  return {
    app: 'claude-code',
    available: true,
    oneClick: false,
    summary: 'Needs a key',
    sections: [
      {
        id: 'claude-code-key',
        title: `Add ${name} with your key`,
        steps: [
          getKeyStep(server, ctx.audience),
          saveKeyStep(server),
          {
            id: 'command',
            text: 'Add the server. The command reads the key from that file.',
            snippet: shell(
              'claude_code',
              'Claude Code command',
              `claude mcp add --transport http --scope user ${server.configName} ${key.serverUrl} --header "${headerFromFile(server)}"`,
            ),
          },
        ],
      },
      ...skillsSection,
    ],
    verify: server.verify,
  };
};

const cursorRecipe = (
  server: AiAppsServer,
  ctx: RecipeContext,
): AiAppRecipe => {
  const { name, serverUrl } = server.profile;
  if (canSignIn(server, 'cursor') && serverUrl) {
    return {
      app: 'cursor',
      available: true,
      oneClick: true,
      summary: 'One click',
      sections: [
        {
          id: 'cursor-add',
          title: `Add ${name} to Cursor`,
          steps: [
            {
              id: 'install',
              text: 'Click **Add to Cursor**, then **Install** when Cursor asks.',
              link: {
                label: 'Add to Cursor',
                href: cursorInstallLink(server.configName, { url: serverUrl }),
              },
              fallbackLink: {
                label: "Doesn't open? Use cursor.com's link",
                href: cursorWebInstallLink(server.configName, {
                  url: serverUrl,
                }),
              },
            },
            {
              id: 'cursor-sign-in',
              text: `When Cursor says **${server.configName}** needs authentication, click it to sign in.`,
            },
            signInStep(server),
          ],
        },
      ],
      verify: server.verify,
    };
  }

  if (!server.keyAccess) {
    return unavailable(server, 'cursor', keyOnlyReason(server, 'cursor'));
  }
  const key = server.keyAccess;
  const blocked = server.signIn?.blocked?.cursor;
  const keyConfig = {
    url: key.serverUrl,
    headers: {
      [key.apiKey.headerName]: envHeaderValue(key.apiKey, key.envVar),
    },
  };
  const keySection: RecipeSection = {
    id: 'cursor-key',
    title: blocked
      ? `If ${key.issuedBy} gave you a ${key.apiKey.keyName}`
      : `Add ${name} to Cursor`,
    tone: blocked ? 'optional' : undefined,
    steps: [
      getKeyStep(server, ctx.audience),
      saveKeyStep(server, true),
      {
        id: 'install',
        text: 'Click **Add to Cursor**, then **Install** when Cursor asks. The link holds no key: Cursor reads it from that variable.',
        link: {
          label: 'Add to Cursor',
          href: cursorInstallLink(server.configName, keyConfig),
        },
        fallbackLink: {
          label: "Doesn't open? Use cursor.com's link",
          href: cursorWebInstallLink(server.configName, keyConfig),
        },
      },
    ],
  };
  if (blocked) {
    return unavailable(server, 'cursor', blocked, [keySection]);
  }
  return {
    app: 'cursor',
    available: true,
    oneClick: true,
    summary: 'Needs a key',
    sections: [keySection],
    verify: server.verify,
  };
};

export const recipeFor = (
  server: AiAppsServer,
  app: AiAppId,
  ctx: RecipeContext,
): AiAppRecipe => {
  switch (app) {
    case 'claude':
      return claudeRecipe(server, ctx);
    case 'chatgpt':
      return chatgptRecipe(server, ctx);
    case 'claude-code':
      return claudeCodeRecipe(server, ctx);
    case 'cursor':
      return cursorRecipe(server, ctx);
  }
};
